/**
 * Which case statuses one version applies to.
 *
 * Three ways of saying it and they are exclusive, so this is one control with a mode rather than
 * three that can contradict each other. The control enforces only what a form control can enforce
 * on its own — that one mode is selected, and that a named list is drawn from the statuses Manage
 * holds. Everything else is the server's: whether a list with nothing in it is admissible, whether
 * a scope makes sense against this rule's shape, and every sentence the user reads. The live
 * validation call answers all of those (see the ESLint rule that stops this file growing any).
 */
import type { ManageTerm } from "~/api/schema";
import type { DraftStatusScope } from "~/features/rules/draft";
import { Field, Select } from "~/ui/Field";

type Mode = "all" | "activity" | "list";

const modeOf = (scope: DraftStatusScope): Mode => {
  if (scope.allStatuses) return "all";
  return scope.activity ? "activity" : "list";
};

const MODES: ReadonlyArray<{ value: Mode; label: string }> = [
  { value: "all", label: "Every status" },
  { value: "activity", label: "Live or dead only" },
  { value: "list", label: "Named statuses" },
];

export function StatusScopeField({
  scope,
  statuses,
  onChange,
}: {
  scope: DraftStatusScope;
  statuses: ManageTerm[];
  onChange: (scope: DraftStatusScope) => void;
}) {
  const mode = modeOf(scope);

  const setMode = (next: Mode) => {
    if (next === "all") {
      onChange({
        allStatuses: true,
        activity: null,
        included: [],
        excluded: [],
      });
      return;
    }
    if (next === "activity") {
      onChange({
        allStatuses: false,
        activity: "Live",
        included: [],
        excluded: [],
      });
      return;
    }
    onChange({
      allStatuses: false,
      activity: null,
      included: [],
      excluded: [],
    });
  };

  const titles = statuses.map((term) => term.title);

  return (
    <>
      <Field
        label="Applies to"
        hint="Every status unless you narrow it — which is what a version recorded before this existed means."
      >
        <Select
          value={mode}
          options={MODES}
          onChange={(next) => setMode(next as Mode)}
        />
      </Field>

      {mode === "activity" ? (
        <Field label="Which activity">
          <Select
            value={scope.activity ?? "Live"}
            options={[
              { value: "Live", label: "Live" },
              { value: "Dead", label: "Dead" },
            ]}
            onChange={(activity) =>
              onChange({
                ...scope,
                activity: activity as "Live" | "Dead",
                allStatuses: false,
              })
            }
          />
        </Field>
      ) : null}

      {mode === "list" ? (
        <Field
          label="Named statuses"
          hint="Drawn from the statuses Manage holds. One this engine invented could never be exported."
        >
          <div role="group" aria-label="Named statuses">
            {titles.map((title) => {
              const checked = scope.included.includes(title);
              return (
                <label key={title} style={{ display: "block" }}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() =>
                      onChange({
                        ...scope,
                        allStatuses: false,
                        activity: null,
                        included: checked
                          ? scope.included.filter((one) => one !== title)
                          : [...scope.included, title],
                      })
                    }
                  />{" "}
                  {title}
                </label>
              );
            })}
          </div>
        </Field>
      ) : null}
    </>
  );
}
